import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import { ConfirmationProgressStatus, setSplitConfirmationProgressByExam } from '../confirmation-progress.js';
import { Conversation } from '../conversation.js';
import { Submission } from '../submission.js';
import { nudgeExamJobs } from './jobs/job-store.js';
import { dispatchJobs } from './jobs/dispatch.js';

export const confirmSubmissionSplitMutation = registerMutation({
  operationName: 'confirmSubmissionSplitMutation',
  type: () => Boolean,
  params: [
    { name: 'examId', type: () => String },
    { name: 'conversationId', type: () => String },
    { name: 'messageId', type: () => String },
  ],
  resolve: (_examId: string, conversationId: string, messageId: string) => async () => {
    const conversationCollection = await getCollection(Conversation);
    const conversation = await conversationCollection.findOne({ _id: conversationId });

    if (!conversation) {
      return false;
    }

    const messages = Array.isArray((conversation as any).messages)
      ? ([...(conversation as any).messages] as any[])
      : [];
    const messageIndex = messages.findIndex(message => String(message?._id ?? message?.id ?? '') === messageId);
    if (messageIndex === -1) {
      return false;
    }

    if (messages[messageIndex]?.confirmed || messages[messageIndex]?.confirmedBy === 'teacher') {
      return true;
    }

    messages[messageIndex] = {
      ...messages[messageIndex],
      confirmed: true,
      confirmedBy: 'teacher',
    };

    await conversationCollection.updateOne(
      { _id: conversationId },
      { $set: { messages: messages as any, lastUpdated: new Date() } },
    );
    await setSplitConfirmationProgressByExam(_examId, {
      status: ConfirmationProgressStatus.DONE,
      confirmed: true,
      message: 'progress.assignmentConfirmed',
    });

    await confirmSplitForExam(_examId);

    return true;
  },
});

/**
 * Freezes the exam's currently-unconfirmed submissions and queues their answer extraction.
 *
 * Shared because there are TWO confirmation paths — this mutation (the canvas button) and
 * `handleConfirmSplitInChat` (the agent tool) — and they had drifted: only the mutation set the
 * per-submission `splitConfirmed` flag, while only the tool started the extraction. That was survivable while
 * extraction ignored the flag. It is not survivable now that `answerExtraction` is gated on it, because the
 * chat path would produce submissions that no extraction is allowed to touch.
 */
export async function confirmSplitForExam(examId: string): Promise<number> {
  // Lock the current batch of submissions for editing. Submissions added afterwards (re-uploads)
  // stay unconfirmed/editable, so the canvas only freezes the parts the user already confirmed.
  const submissionCollection = await getRawCollection(Submission._collectionName);

  // Read the ids BEFORE the write: `updateMany` reports a count, not which documents it touched, and the
  // ids are exactly what the extraction jobs are keyed on. Safe as a read-then-write because the teacher's
  // confirmation is the only thing that sets this flag.
  const newlyConfirmed = await submissionCollection
    .find({ examId, splitConfirmed: { $ne: true } }, { projection: { _id: 1 } })
    .toArray();

  await submissionCollection.updateMany({ examId, splitConfirmed: { $ne: true } }, { $set: { splitConfirmed: true } });

  // CONFIRMING THE SPLIT IS WHAT CREATES THE ANSWER-EXTRACTION WORK, rather than the upload doing it and
  // the job then waiting for a gate. Two reasons that is the better place:
  //
  //  - "may this be extracted?" is a fact about the SUBMISSION (`splitConfirmed`), not about the exam, and
  //    a job's `ready()` only ever sees the exam. Enqueuing here means the job exists only once the answer
  //    is already yes, so no gate has to be evaluated against the wrong document.
  //  - re-uploads work without any state having to be flipped back. New submissions arrive unconfirmed,
  //    this query picks up exactly them at the next confirmation, and the previously confirmed ones are
  //    neither re-enqueued (their jobs are long done) nor disturbed.
  //
  // One call, and the type's own `batch` owns the progress record and the fan-out. Enqueue failures are swallowed in
  // there for this site's sake: the split IS confirmed at this point and the mutation must report that truthfully.
  await dispatchJobs('answerExtraction', examId, {
    submissionIds: newlyConfirmed.map(submission => String((submission as any)._id)),
  });

  // Wakes rows that were already deferred on a gate this confirmation may have opened — see the same call in
  // `confirm-structure.ts` for why the enqueues above cannot do it. Last, so the state is committed first.
  await nudgeExamJobs(examId);

  return newlyConfirmed.length;
}
